import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { collectFontFaces, compositionToPostcardHtml } from '../createEditor/kinemaToPostcard';
import type { MailPieceSize } from '../createEditor/mailPieceTemplates';
import { encodeArtwork } from '@/shared/utils/artworkTransport';
import { FontService } from '@services/font/FontService';
import {
  Editor,
  EditorProvider,
  UPDATE_PROPERTY,
  getCompositionProps,
  pathPointsInterpolator,
} from '@/lib/kinema';
import { EditorActionsProvider, type EditorActions } from '@/modules/kinema/shell/editorActionsContext';
import { NewProjectModal, type NewProjectConfig } from '@/modules/kinema/shell/NewProjectModal';
import { KinemaShell } from '@/modules/kinema/shell/KinemaShell';
import { AssetKitProvider } from '@/modules/kinema/assetKit';
import { brandToAssetKit } from './assetKitAdapters';
import { useBrandColors } from './useBrandColors';
import { BRAND_IDENTITY_V2, useFeatureFlag } from '@/shared/featureFlags/useFeatureFlag';
import { MediaOwnerProvider } from '@/modules/kinema/mediaOwnerContext';
import { loadFontsFromState } from '@/modules/kinema/fonts';
import { MediaService } from '@services/media/MediaService';
import { getLayeredMedia, createLayeredMedia, updateLayeredMedia, updateLayeredMediaPreview, spawnLayeredMediaRender } from '@/modules/kinema/layeredMediaService';
import { createPreviewCapture } from '@/modules/createStudio/previewCapture';
import { appStore } from '@store/appStore';
import { ContentCatalogService } from '@services/contentCatalog/ContentCatalogService';
import { OrganizationService } from '@services/organizations/OrganizationService';
import type { Brand } from '@models/brand.model';
import { contentEditAllowed, type AdLock } from '@models/Ad.model';
import AdLockedDialog from '@/modules/createEditor/components/AdLockedDialog';
import { useSearchParams, useParams } from 'react-router-dom';
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle';
import { EditorSidebar } from '@/modules/kinema/shell/EditorSidebar';
import type { Ad } from '@models/Ad.model';

// ─── Editor Instance ──────────────────────────────────────
function createPlaygroundEditor(canvas?: { width: number; height: number }, frameRate?: number) {
  const editor = new Editor({
    canvas: canvas ?? { width: 1, height: 1 },
    frameRate,
    middleware: [],
    devtools: true,
  });
  editor.registerInterpolator('pathPoints', pathPointsInterpolator);
  return editor;
}

// Upload can fail on a transient network blip; retry a few times so the
// follow-up ad update (which is what removes the video) isn't silently skipped.
async function uploadWithRetry<T>(upload: () => Promise<T>, attempts = 3): Promise<T> {
  let lastErr: unknown;
  for (let i = 0; i < attempts; i++) {
    try {
      return await upload();
    } catch (err) {
      lastErr = err;
      if (i < attempts - 1) await new Promise((r) => setTimeout(r, 300 * (i + 1)));
    }
  }
  throw lastErr;
}
// ─── Entry Point ──────────────────────────────────────────

/**
 * The canvas one side of a card is authored on, in pixels.
 *
 * Bleed size at 300dpi -- the artwork we send, before Lob trims 0.125in off every edge.
 * Mirrors `CANVAS` in mailPieceTemplates.ts and `CANVAS` in the server's
 * cmd_mail_piece_side.py; all three have to agree or a card is laid out against a size it
 * will not be printed at.
 */
const MAIL_DPI = 300;
const MAIL_CANVAS: Record<string, { width: number; height: number }> = {
  '4x6': { width: 6.25 * MAIL_DPI, height: 4.25 * MAIL_DPI },
  '6x9': { width: 9.25 * MAIL_DPI, height: 6.25 * MAIL_DPI },
  '6x11': { width: 11.25 * MAIL_DPI, height: 6.25 * MAIL_DPI },
};

function mailCanvas(size: string | undefined) {
  return MAIL_CANVAS[size ?? '4x6'] ?? MAIL_CANVAS['4x6'];
}

export default function AdEditor() {
  const { adId: routeAdId } = useParams<{ adId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  /*
      Which face of a postcard this is.

      A card has two sides and the editor has one canvas, so each side is its own
      composition and the side travels in the URL. Absent for every other ad type, which
      has only one thing to edit.
  */
  const mailSide = (searchParams.get('side') as 'front' | 'back' | null) ?? null;

  const [project, setProject] = useState<{ name: string; width: number; height: number } | null>(null);
  const [editor, setEditor] = useState<Editor | null>(null);
  const [fontsReady, setFontsReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [rendering, setRendering] = useState(false);
  const [renderError, setRenderError] = useState<string | null>(null);
  const renderingRef = useRef(false);
  const adRef = useRef<Ad | null>(null);
  const mailSideRef = useRef<string | null>(null);
  const [mailHtmlError, setMailHtmlError] = useState<string | null>(null);
  const [brand, setBrand] = useState<Brand | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [staticMode, setStaticMode] = useState(false);
  // The loaded ad, kept in state for the sidebar (MPN-1631). The refs above are for
  // callbacks that must not re-render the editor; this one has to.
  const [ad, setAd] = useState<Ad | null>(null);
  useDocumentTitle(project?.name ?? "Ad Editor");
  // ─── Content lock (MPN-1390) ───
  // The composition of an approved/published ad can't be saved unless the user confirms.
  // Confirmation is held for the life of this editor: autosave and the save-on-close both
  // run without a user present, and re-prompting there is impossible.
  const [lock, setLock] = useState<AdLock | null>(null);
  const [lockPromptOpen, setLockPromptOpen] = useState(false);
  const lockRef = useRef<AdLock | null>(null);
  const forceEditRef = useRef(false);
  const adNameRef = useRef<string>("");
  const adIdRef = useRef<number | null>(routeAdId ? parseInt(routeAdId, 10) : null);
  const mediaIdRef = useRef<number | null>(null);
  const editorRef = useRef<Editor | null>(null);
  const mainLayerRef = useRef<any>(null);
  const capturePreviewRef = useRef(
    createPreviewCapture({
      minIntervalMs: 5000,
      uploadMedia: (file, name) => new MediaService().uploadMedia(file, name),
      updatePreview: updateLayeredMediaPreview,
    })
  );

  // Keep editorRef in sync
  useEffect(() => { editorRef.current = editor; }, [editor]);

  // Load from ad → resolve layered media → load editor
  /*
      A side switch is a different composition, not a different view of one. The loader
      below refuses to run while an editor exists, so the old one is cleared first.
  */
  const loadedSideRef = useRef<string | null>(null);
  useEffect(() => {
    if (loadedSideRef.current !== null && loadedSideRef.current !== mailSide) {
      setEditor(null);
      setProject(null);
    }
    loadedSideRef.current = mailSide;
    mailSideRef.current = mailSide;
  }, [mailSide]);

  useEffect(() => {
    if (!routeAdId || editor) return;
    const adId = parseInt(routeAdId, 10);
    if (isNaN(adId)) return;

    const ccService = new ContentCatalogService();
    ccService.getAd(adId, ['content'])
      .then(async (ad) => {
        adIdRef.current = ad.id;
        adRef.current = ad;
        adNameRef.current = ad.name || "";
        setAd(ad);
        lockRef.current = ad.lock;
        setLock(ad.lock);
        const content = ad.content as any;
        const isMailPiece = ad.type === 'direct_mail';
        const isImage = ad.type === 'image' || isMailPiece;

        /*
            A mail piece keeps a composition per side, and creates one on first open: cards
            made before postcards were editable have artwork and no compositions at all, and
            they still have to print. Everything else has exactly one.
        */
        let lmId = content?.layered_media_id;
        if (isMailPiece) {
          const side = mailSide ?? 'front';
          const opened = await ccService.openMailPieceSide(ad.id, side);
          lmId = opened.id;
        }

        if (!lmId) {
          setLoadError('This ad has no layered media.');
          return;
        }

        const lm = await getLayeredMedia(lmId);
        const data = lm.data;
        const hasData = data?.layers && data?.rootLayerId;

        if (hasData) {
          // Existing data — load it
          const root = data.layers[data.rootLayerId];
          const e = createPlaygroundEditor({ width: root?.width ?? 1920, height: root?.height ?? 1080 });
          e.loadState(data);
          mediaIdRef.current = lmId;
          setStaticMode(lm.type === 'image');
          setEditor(e);
          setProject({ name: root?.name ?? 'Untitled', width: root?.width ?? 1920, height: root?.height ?? 1080 });
        } else {
          /*
              A fresh canvas. 1080x1080 is a square and no postcard is: a mail piece opens at
              its own bleed size instead, which the server already used when it created the
              composition. Falling through to the square would lose a quarter inch to the
              trim on every edge and nothing on screen would say so.
          */
          const blank = isMailPiece
            ? mailCanvas(content?.size)
            : { width: 1080, height: 1080 };
          const e = createPlaygroundEditor(blank);
          e.setDuration(isImage ? 1 : 10);
          mediaIdRef.current = lmId;
          setStaticMode(isImage);
          setEditor(e);
          setProject({ name: ad.name || 'Untitled', ...blank });

          // Save the initial state to the existing layered media. A locked ad with no
          // composition yet is odd but possible, and the 409 must not stop the editor
          // from opening — the user can still look, and save() will prompt.
          try {
            await updateLayeredMedia(lmId, { data: e.toJSON() });
          } catch (err) {
            console.warn('Could not seed initial composition:', err);
          }
        }
      })
      .catch((err) => {
        console.error('Failed to load ad:', err);
        setLoadError('Failed to load project');
      });
    /*
        `editor` is a dependency, not just a guard.

        Switching side clears the editor so this can rebuild it. Without `editor` here, the
        clear and this effect run in the same pass: the guard above still sees the old editor
        and bails, the clear lands afterwards, and nothing re-runs because neither `routeAdId`
        nor `mailSide` changed again. The editor stays null and the screen says "Loading..."
        for ever -- which is exactly what Rachel hit switching to the back of a card.
    */
  }, [routeAdId, mailSide, editor]);

  const handleProjectCreated = (cfg: NewProjectConfig) => {
    const e = createPlaygroundEditor({ width: cfg.width, height: cfg.height }, cfg.frameRate);
    e.setDuration(cfg.duration);
    e.dispatchCommand(UPDATE_PROPERTY, { layerId: e.getState().rootLayerId, property: 'name', value: cfg.name });
    setEditor(e);
    setProject({ name: cfg.name, width: cfg.width, height: cfg.height });
  };

  /*
      Turn the side that was just saved into the artwork the printer will render.

      The composition is the thing somebody edits; the HTML on the ad is the thing Lob
      renders. Keeping them in step on every save is what stops a card looking right in the
      editor and printing something else -- and that gap is only discoverable a week later,
      from the post.

      Failures here are reported, not swallowed and not fatal: the composition itself is
      already saved, so the work is safe, and the artwork can be regenerated by saving again.
  */
  const syncMailPieceHtml = useCallback(async (data: any) => {
    const current = adRef.current;
    if (!current || current.type !== 'direct_mail') return;

    const content = current.content as any;
    const size = (content?.size ?? '4x6') as MailPieceSize;
    const side = (mailSideRef.current ?? 'front') as 'front' | 'back';

    try {
      const fontService = new FontService();
      // Fetched here rather than inside the converter so that stays pure, and so a font
      // service that is down costs a substitution rather than the whole save.
      const fontFaces = await collectFontFaces(data, (family, weight, italic) =>
        fontService.getFontCss(family, weight, italic).catch(() => null));

      const { html } = compositionToPostcardHtml(data as any, { size, fontFaces });
      if (!html) return;

      await new ContentCatalogService().updateAd(current.id, {
        content: {
          size,
          // Encoded: a WAF rule rejects any body containing CSS before it reaches Django,
          // and every real mail piece is styled HTML.
          ...(side === 'front'
            ? { front_b64: encodeArtwork(html) }
            : { back_b64: encodeArtwork(html) }),
        },
      } as any);
    } catch (err) {
      console.error('Could not update the printed artwork for this side:', err);
      setMailHtmlError('Saved the design, but could not update the printed artwork. Save again.');
    }
  }, []);

  // ─── Save logic ──────────────────────────────────────────
  // Returns whether the composition actually reached the server, so callers that depend
  // on a saved state (render) can bail instead of shipping stale data.
  const save = useCallback(async (): Promise<boolean> => {
    const e = editorRef.current;
    if (!e || saving) return false;

    // Locked and unconfirmed: ask instead of saving. The dialog re-enters save().
    if (!contentEditAllowed(lockRef.current, forceEditRef.current)) {
      setLockPromptOpen(true);
      return false;
    }

    setSaving(true);
    try {
      const data = e.toJSON();
      const state = e.getState();
      const name = state.layers[state.rootLayerId]?.name || 'Untitled';

      if (mediaIdRef.current) {
        await updateLayeredMedia(mediaIdRef.current, { name, data, force_edit: forceEditRef.current });
        /*
            The composition is what somebody edits; the HTML is what Lob prints. Regenerated
            on every save so the two cannot drift -- a card that looks right in the editor
            and prints last week's artwork is the failure this avoids.
        */
        await syncMailPieceHtml(data);
      } else {
        const orgId = appStore.loggedInUser.organization.id;
        const result = await createLayeredMedia({ name, organization_id: orgId, data });
        mediaIdRef.current = result.id;
      }
      setLastSaved(new Date());

      // Skip the throttled preview capture while a render is in progress —
      // it mutates the same Konva stage transform as the render capture and
      // would race with it on the shared stage.
      const main = mainLayerRef.current;
      if (mediaIdRef.current && main && !renderingRef.current) {
        const composition = getCompositionProps(e.getState());
        capturePreviewRef.current(mediaIdRef.current, main, composition).catch(() => {});
      }
    } catch (err) {
      console.error('Failed to save:', err);
      setSaving(false);
      return false;
    }
    setSaving(false);
    return true;
  }, [saving]);

  const handleEditAnyway = useCallback(() => {
    // Held for the rest of this editor session: the save-on-close and the throttled
    // autosave have no user to ask.
    forceEditRef.current = true;
    setLockPromptOpen(false);
    void save();
  }, [save]);

  // ─── Render logic ────────────────────────────────────────
  const renderComposition = useCallback(async () => {
    if (rendering) return;

    setRendering(true);
    setRenderError(null);
    renderingRef.current = true;
    try {
      // Save first — if the content lock blocked it, the prompt is now up and rendering
      // stale data would put the wrong creative on the network.
      if (!(await save())) {
        setRendering(false);
        renderingRef.current = false;
        return;
      }

      const id = mediaIdRef.current;
      if (!id) {
        console.error('Cannot render: no layered media ID');
        return;
      }
      if (staticMode) {
        // Static/image: capture main layer as PNG and upload
        const mainLayer = mainLayerRef.current;
        if (!mainLayer) return;

        const e = editorRef.current;
        const comp = e ? getCompositionProps(e.getState()) : { width: 1920, height: 1080 };

        // Temporarily reset stage transform to capture at native resolution
        const stage = mainLayer.getStage();
        const prevScale = stage?.scaleX() ?? 1;
        const prevX = stage?.x() ?? 0;
        const prevY = stage?.y() ?? 0;
        stage?.scale({ x: 1, y: 1 });
        stage?.position({ x: 0, y: 0 });

        const blob: Blob = await new Promise((resolve, reject) => {
          mainLayer.toBlob({
            x: 0, y: 0,
            width: comp.width,
            height: comp.height,
            mimeType: 'image/png',
            quality: 1,
            pixelRatio: 1,
            callback: (b: Blob | null) => {
              // Restore stage transform
              stage?.scale({ x: prevScale, y: prevScale });
              stage?.position({ x: prevX, y: prevY });
              stage?.batchDraw();
              b ? resolve(b) : reject(new Error('Capture failed'));
            },
          });
        });

        const file = new File([blob], 'render.png', { type: 'image/png' });
        const mediaService = new MediaService();

        // Critical path: the image must upload before we can point the ad at
        // it. Retry transient failures so the updateAd below (the only thing
        // that swaps the ad's content from video → image) isn't silently
        // skipped, and surface a hard failure instead of failing silently.
        let uploaded;
        try {
          uploaded = await uploadWithRetry(() =>
            mediaService.uploadMedia(file, 'render.png', { width: comp.width, height: comp.height })
          );
        } catch (err) {
          console.error('Render image upload failed after retries:', err);
          setRenderError('Render failed — the image could not be uploaded. Please try again.');
          return;
        }

        if (adIdRef.current) {
          try {
            const ccService = new ContentCatalogService();
            await ccService.updateAd(adIdRef.current, {
              content: { image_id: Number(uploaded.id) },
            });
          } catch (err) {
            console.error('Failed to update ad with rendered image:', err);
            setRenderError('Image uploaded but the ad could not be updated. Please try again.');
            return;
          }
        }
      } else {
        // Video: try to upload current frame as thumbnail (may fail if canvas is tainted by cross-origin media)
        const mainLayer = mainLayerRef.current;
        if (mainLayer && adIdRef.current) {
          try {
            const e = editorRef.current;
            const comp = e ? getCompositionProps(e.getState()) : { width: 1920, height: 1080 };
            const stage = mainLayer.getStage();
            const ps = stage?.scaleX() ?? 1;
            const px = stage?.x() ?? 0;
            const py = stage?.y() ?? 0;
            stage?.scale({ x: 1, y: 1 });
            stage?.position({ x: 0, y: 0 });

            const thumbBlob: Blob | null = await new Promise((resolve) => {
              try {
                mainLayer.toBlob({
                  x: 0, y: 0, width: comp.width, height: comp.height,
                  mimeType: 'image/jpeg', quality: 0.8, pixelRatio: 0.5,
                  callback: (b: Blob | null) => {
                    stage?.scale({ x: ps, y: ps });
                    stage?.position({ x: px, y: py });
                    stage?.batchDraw();
                    resolve(b);
                  },
                });
              } catch {
                stage?.scale({ x: ps, y: ps });
                stage?.position({ x: px, y: py });
                stage?.batchDraw();
                resolve(null); // tainted canvas — can't export
              }
            });
            if (thumbBlob) {
              const thumbFile = new File([thumbBlob], 'thumbnail.jpg', { type: 'image/jpeg' });
              const mediaService = new MediaService();
              const thumbMedia = await mediaService.uploadMedia(thumbFile, 'thumbnail.jpg');
              const ccService = new ContentCatalogService();
              await ccService.updateAd(adIdRef.current, {
                content: { thumbnail_id: Number(thumbMedia.id) },
              });
            }
          } catch (err) {
            console.warn('Thumbnail upload failed (non-critical):', err);
          }
        }

        await spawnLayeredMediaRender(id);
      }
    } catch (err) {
      console.error('Failed to render:', err);
    } finally {
      renderingRef.current = false;
      setRendering(false);
    }
  }, [rendering, save, staticMode]);

  // Cmd+S / Ctrl+S to save
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        save();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [save]);

  // Save on unmount (close)
  useEffect(() => {
    return () => {
      const e = editorRef.current;
      if (!e) return;
      // Never write to locked content without the user having said so — there is nobody
      // to ask during unmount, so an unconfirmed close discards instead of overwriting.
      if (!contentEditAllowed(lockRef.current, forceEditRef.current)) return;
      const data = e.toJSON();
      const state = e.getState();
      const name = state.layers[state.rootLayerId]?.name || 'Untitled';
      const id = mediaIdRef.current;

      if (id) {
        updateLayeredMedia(id, { name, data, force_edit: forceEditRef.current }).catch(() => {});
      } else {
        const orgId = appStore.loggedInUser.organization.id;
        createLayeredMedia({ name, organization_id: orgId, data }).catch(() => {});
      }
    };
  }, []);

  // Load all fonts used by text layers before first render
  useEffect(() => {
    if (!editor) return;
    loadFontsFromState(editor.getState()).then(() => setFontsReady(true));
  }, [editor]);

  // When a font finishes loading, poke Konva text nodes to recalculate word wrap
  useEffect(() => {
    const handler = () => {
      const stage = mainLayerRef.current?.getStage?.();
      if (!stage) return;
      stage.find('Text').forEach((node: any) => {
        node._setTextData();
      });
      stage.batchDraw();
    };
    document.fonts.addEventListener('loadingdone', handler);
    return () => document.fonts.removeEventListener('loadingdone', handler);
  }, []);

  // Fetch org brand. An optional `organizationId` query param overrides the
  // logged-in user's org (e.g. when opened from the engagement tool, where
  // branding should follow the launch's organization rather than the viewer).
  const brandOrgId = searchParams.get('organizationId') ?? String(appStore.loggedInUser.organization.id);
  useEffect(() => {
    if (brandOrgId === '-1') return;
    const orgService = new OrganizationService();
    orgService.getOrganizationBrand(brandOrgId).then(setBrand).catch(() => {});
  }, [brandOrgId]);
  const brandColors = useBrandColors(brandOrgId, brand);
  const brandIdentityV2 = useFeatureFlag(BRAND_IDENTITY_V2);

  // The shell only needs "a save was attempted"; the boolean matters to renderComposition,
  // which calls save() directly and must not render an unsaved composition.
  const actions: EditorActions = {
    saving, lastSaved, save: async () => { await save(); },
    rendering, render: renderComposition, renderError, staticMode,
  };
  const assetKit = useMemo(
    () => brandToAssetKit(brand, brandColors, { typography: brandIdentityV2 }),
    [brand, brandColors, brandIdentityV2],
  );

  if (loadError) {
    return (
      <div style={{
        height: '100vh', background: '#ffffff', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        color: '#ef4444', fontSize: 14,
      }}>
        {loadError}
      </div>
    );
  }

  if (!routeAdId && (!project || !editor)) {
    return (
      <div style={{
        height: '100vh', background: '#020617', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
      }}>
        <NewProjectModal onCreated={handleProjectCreated} />
      </div>
    );
  }

  if (!editor || !fontsReady) {
    return (
      <div style={{
        height: '100vh', background: '#ffffff', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        color: '#94a3b8', fontSize: 14,
      }}>
        Loading...
      </div>
    );
  }

  return (
    <AssetKitProvider value={assetKit}>
      <MediaOwnerProvider value={appStore.loggedInUser.id}>
        <EditorActionsProvider value={actions}>
          <EditorProvider editor={editor}>
            {/*
                Which face of the card you are on, and how you get to the other one.

                Rachel: "I only have one 'box' -- where would I create the back?" A card has
                two sides and the editor has one canvas, so the answer has to be visible on
                the canvas rather than implied. Switching saves first: the two sides are
                separate compositions and leaving one unsaved to open the other loses work.
            */}
            {ad?.type === 'direct_mail' && (
              <div style={{
                position: 'fixed', top: 10, left: '50%', transform: 'translateX(-50%)',
                zIndex: 60, display: 'flex', alignItems: 'center', gap: 2, padding: 2,
                background: '#ffffff', border: '1px solid #e2e8f0',
                borderRadius: 8, boxShadow: '0 1px 3px rgba(15,23,42,0.10)',
              }}>
                {/*
                    Labelled, because "Back" on its own reads as going back.

                    Rachel described pressing it as clicking "back in the editor" -- next to
                    an editor that has its own back arrow, a bare Front/Back pair is a
                    navigation control until proven otherwise.
                */}
                <span style={{
                  fontSize: 11, fontWeight: 600, color: '#94a3b8',
                  padding: '0 8px 0 10px', letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                }}>
                  Side
                </span>
                {(['front', 'back'] as const).map((s) => {
                  const active = (mailSide ?? 'front') === s;
                  return (
                    <button
                      key={s}
                      type="button"
                      disabled={active || saving}
                      onClick={async () => {
                        await save();
                        setSearchParams((prev) => {
                          const next = new URLSearchParams(prev);
                          next.set('side', s);
                          return next;
                        });
                      }}
                      style={{
                        border: 'none', borderRadius: 6, cursor: active ? 'default' : 'pointer',
                        padding: '5px 16px', fontSize: 12, fontWeight: 600,
                        textTransform: 'capitalize',
                        background: active ? '#4f46e5' : 'transparent',
                        color: active ? '#fff' : '#475569',
                      }}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
            )}

            {mailHtmlError && (
              <div style={{
                position: 'fixed', top: 48, left: '50%', transform: 'translateX(-50%)',
                zIndex: 60, background: '#fef3c7', color: '#92400e',
                border: '1px solid #fcd34d', borderRadius: 6,
                padding: '6px 12px', fontSize: 12,
              }}>
                {mailHtmlError}
              </div>
            )}

            <KinemaShell
              onMainLayerReady={(layer) => { mainLayerRef.current = layer; }}
              sidebar={ad ? <EditorSidebar ad={ad} /> : undefined}
            />
            <AdLockedDialog
              show={lockPromptOpen}
              adName={adNameRef.current || undefined}
              lock={lock}
              replacesInAdGroup={false}
              onEditAnyway={handleEditAnyway}
              onClose={() => setLockPromptOpen(false)}
            />
          </EditorProvider>
        </EditorActionsProvider>
      </MediaOwnerProvider>
    </AssetKitProvider>
  );
}

// ─── Styles ───────────────────────────────────────────────
